import { forwardRef } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * Canonical text input.
 *
 * Single source of truth for input chrome — height, border, focus ring,
 * placeholder colour, padding. Pages used to inline this className
 * across modals, toolbars, settings forms; replace those with `<Input/>`
 * (or `<SearchInput/>` for the search-with-icon variant).
 *
 * Use `invalid` (not the native `aria-invalid` attribute alone) to flip
 * the border to the critical tone so error states render the same way
 * everywhere. Pair with `<Field/>` for label + helper-text layout.
 */
export type InputSize = "sm" | "md";

// Input text is held to a readable floor (md = 14px) — typed values are
// primary content and were the hardest thing to read at the old 12.5px.
const SIZE_CLS: Record<InputSize, string> = {
  sm: "h-7 px-2 text-[13px]",
  md: "h-8 px-2.5 text-[14px]",
};

export type InputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> & {
  size?: InputSize;
  invalid?: boolean;
  /** Optional leading icon — adds left padding so text doesn't collide. */
  leadingIcon?: LucideIcon;
};

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { size = "md", invalid, leadingIcon: Icon, className, ...rest },
  ref,
) {
  return (
    <div className={cn("relative", rest.disabled && "opacity-60")}>
      {Icon && (
        <Icon
          size={12}
          aria-hidden
          className="absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-muted pointer-events-none"
        />
      )}
      <input
        ref={ref}
        aria-invalid={invalid || undefined}
        className={cn(
          // Placeholder kept at the dim tone (not muted) so it reads as
          // guidance, not noise; resting border uses the brighter `line`
          // token and the focus border lifts to brand so the active field
          // is unmistakable.
          "w-full rounded-md bg-bg-base border placeholder:text-fg-dim text-fg",
          "focus-ring focus-visible:border-brand-400",
          "disabled:cursor-not-allowed",
          invalid ? "border-status-critical/50" : "border-line",
          SIZE_CLS[size],
          Icon && "pl-7",
          className,
        )}
        {...rest}
      />
    </div>
  );
});
